import React, { useState, useEffect, useCallback } from 'react';
import { getCourseEnrollments } from '../api';
import './CertificatesManager.css';

const CertificatesManager = ({ isOpen, onClose, courseId = null, courseTitle = null }) => {
  const [completedLearners, setCompletedLearners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchCompletedLearners = useCallback(async () => {
    if (!courseId) {
      setError('No course selected');
      setCompletedLearners([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const enrollments = await getCourseEnrollments(courseId);

      console.log('Raw enrollments data:', enrollments);
      console.log('Total enrollments:', enrollments.length);

      // Filter to only completed enrollments - check ALL possible completion indicators
      const completed = enrollments.filter(e => {
        // Check various status fields
        const hasCompletedStatus =
          e.status === 'completed' ||
          e.enrollment_status === 'completed' ||
          e.enrolment_status === 'completed' ||
          (typeof e.status === 'string' && e.status.toLowerCase() === 'completed');

        // Check completion date fields
        const hasCompletionDate =
          (e.completed_at != null && e.completed_at !== '') ||
          (e.completion_date != null && e.completion_date !== '') ||
          (e.completedAt != null && e.completedAt !== '');

        // Check progress fields
        const hasFullProgress =
          e.progress === 100 ||
          e.progress_percentage === 100 ||
          e.progressPercentage === 100;

        // Check if marked as completed in other ways
        const isCompleted = e.completed === true || e.completed === 1 || e.is_completed === true;

        const result = hasCompletedStatus || hasCompletionDate || hasFullProgress || isCompleted;

        if (result) {
          console.log('Completed enrollment found:', {
            id: e.id,
            status: e.status,
            completed_at: e.completed_at,
            progress: e.progress,
            learner: e.learner?.full_name || e.staff?.firstname
          });
        }

        return result;
      });

      console.log('Completed enrollments:', completed.length);

      setCompletedLearners(completed);
    } catch (err) {
      console.error('Error fetching completed learners:', err);
      setError(err.message || 'Failed to load completed learners');
      setCompletedLearners([]);
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    if (isOpen && courseId) {
      fetchCompletedLearners();
    }
  }, [isOpen, courseId, fetchCompletedLearners]);

  const filteredLearners = completedLearners.filter(learner => {
    const searchLower = searchTerm.toLowerCase();
    const name = (learner.learner?.full_name || 
                  learner.learner?.name ||
                  `${learner.learner?.firstname || ''} ${learner.learner?.lastname || ''}`.trim() ||
                  learner.name ||
                  '').toLowerCase();
    const email = (learner.learner?.email || learner.email || '').toLowerCase();
    const phone = (learner.learner?.phone_number || learner.phone || '').toLowerCase();
    
    return name.includes(searchLower) || 
           email.includes(searchLower) || 
           phone.includes(searchLower);
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="certificates-manager-overlay" onClick={onClose}>
      <div className="certificates-manager-sidebar" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="certificates-manager-header">
          <div className="certificates-manager-title">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <div>
              <h2>{courseTitle || 'Certificates'}</h2>
              <p className="certificates-subtitle">Completed learners for this course</p>
            </div>
            <span className="certificates-count">{completedLearners.length}</span>
          </div>
          <button className="certificates-manager-close" onClick={onClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Search Bar */}
        <div className="certificates-manager-search">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder="Search learners..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="certificates-search-input"
          />
          {searchTerm && (
            <button
              className="certificates-search-clear"
              onClick={() => setSearchTerm('')}
              aria-label="Clear search"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        {/* Content */}
        <div className="certificates-manager-content">
          {loading ? (
            <div className="certificates-loading">
              <div className="certificates-spinner"></div>
              <p>Loading certificates...</p>
            </div>
          ) : error ? (
            <div className="certificates-error">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <p>{error}</p>
              <button className="certificates-retry-btn" onClick={fetchCompletedLearners}>
                Retry
              </button>
            </div>
          ) : filteredLearners.length === 0 ? (
            <div className="certificates-empty">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <p>{searchTerm ? 'No learners match your search' : 'No one has completed this course yet'}</p>
            </div>
          ) : (
            <div className="certificates-list">
              {filteredLearners.map((enrollment, index) => {
                const learner = enrollment.learner || enrollment.staff || enrollment;
                const name = learner.full_name ||
                            learner.name ||
                            `${learner.firstname || ''} ${learner.lastname || ''}`.trim() ||
                            'Unknown Learner';
                const email = learner.email || enrollment.email || 'N/A';
                const phone = learner.phone_number || learner.phone || enrollment.phone || 'N/A';
                const completedDate = enrollment.completed_at ||
                                     enrollment.updated_at ||
                                     enrollment.completedAt ||
                                     'N/A';

                // Extract facility name
                const facility = learner.facility?.name ||
                                learner.facility?.title ||
                                learner.facility_name ||
                                (typeof learner.facility === 'string' ? learner.facility : null) ||
                                'N/A';

                // Extract cadre/job title
                const cadre = learner.cadre?.name ||
                             learner.cadre?.title ||
                             learner.job_title?.name ||
                             learner.job_title?.title ||
                             learner.jobtitle?.name ||
                             learner.job_title_name ||
                             (typeof learner.cadre === 'string' ? learner.cadre : null) ||
                             (typeof learner.job_title === 'string' ? learner.job_title : null) ||
                             'N/A';

                return (
                  <div key={enrollment.id || index} className="certificate-card">
                    <div className="certificate-card-header">
                      <div className="certificate-avatar">
                        {name.charAt(0).toUpperCase()}
                      </div>
                      <div className="certificate-info">
                        <h3 className="certificate-name">{name}</h3>
                        <p className="certificate-meta">
                          <span>{email}</span>
                          {phone !== 'N/A' && <span> • {phone}</span>}
                        </p>
                      </div>
                      <div className="certificate-badge-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                          <polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                      </div>
                    </div>
                    <div className="certificate-card-body">
                      <div className="certificate-detail-item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                        </svg>
                        <span><strong>Course:</strong> {courseTitle || 'N/A'}</span>
                      </div>
                      {facility !== 'N/A' && (
                        <div className="certificate-detail-item">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5m-4 0h4" />
                          </svg>
                          <span><strong>Facility:</strong> {facility}</span>
                        </div>
                      )}
                      {cadre !== 'N/A' && (
                        <div className="certificate-detail-item">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                          </svg>
                          <span><strong>Cadre:</strong> {cadre}</span>
                        </div>
                      )}
                      <div className="certificate-detail-item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12 6 12 12 16 14" />
                        </svg>
                        <span><strong>Completed:</strong> {formatDate(completedDate)}</span>
                      </div>
                      {enrollment.progress !== undefined && (
                        <div className="certificate-detail-item">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                          </svg>
                          <span><strong>Progress:</strong> {enrollment.progress || enrollment.progress_percentage || 100}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="certificates-manager-footer">
          <button className="certificates-refresh-btn" onClick={fetchCompletedLearners} disabled={loading}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
            </svg>
            Refresh
          </button>
          <div className="certificates-footer-info">
            Showing {filteredLearners.length} of {completedLearners.length} completed learners
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificatesManager;





